<template>
  <v-dialog v-model="value" max-width="650px" persistent>
    <v-card height="600px" v-if="!historicalTestPlanStatusResult">
      <v-card-title><span class="headline">Edit Testing Statuses</span> </v-card-title>
      <v-card-subtitle class="mt-2 font-weight-bold primary--text">
        Option 1: Set testing statuses to "Historically Not Impacted" based on Go Live Date
      </v-card-subtitle>
      <v-card-text>
        <p>
          By selecting this action, any testing status of "Not Started" will be set to "Historically Not Impacted" if
          the release note is from a pack with a UAT period before the Go Live date of
          {{ formatGoLiveDate(serviceAreaRecord.goLiveDate) }}.
        </p>
        <p>
          If a Go Live Date has not been set, this action will not be available.
        </p>
        <div>
          <v-btn
            @click="update"
            class="ml-2"
            color="primary"
            data-cy="saveButton"
            :disabled="!serviceAreaRecord.goLiveDate"
            outlined
            small
            >Update</v-btn
          >
        </div>
      </v-card-text>
      <v-divider class="mx-10 mb-6 mt-4"></v-divider>
      <v-card-subtitle class="font-weight-bold primary--text">
        Option 2: Reset testing statuses marked "Historically Not Impacted" to "Not Started"
      </v-card-subtitle>
      <v-card-text>
        <p>
          This action allows you to reverse the action of Option 1. It will reset
          <span class="font-italic font-weight-bold">all</span> testing statuses marked as "Historically Not Impacted"
          to "Not Started", regardless of the Go Live Date.
        </p>
        <p>If no records have been set to "Historically Not Impacted", this action will not be available.</p>
        <p>
          Currently, {{ historicallyNotImpactedCount }} records have a testing status of "Historically Not Impacted."
        </p>
        <div>
          <v-btn
            @click="reset"
            class="ml-2"
            color="primary"
            data-cy="resetButton"
            :disabled="!historicallyNotImpactedCount"
            outlined
            small
            >Reset</v-btn
          >
        </div>
      </v-card-text>
      <v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="close" data-cy="closeButton" outlined small>Close</v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
    <v-card height="525px" v-else>
      <v-card-title><span class="headline">Results</span> </v-card-title>
      <v-card-text>
        <generic-loader caption="Updating records..." topMargin="2" v-if="isLoading"></generic-loader>
        <p v-if="historicalTestPlanStatusResult">
          {{ historicalTestPlanStatusResult }}
        </p>
      </v-card-text>

      <v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="close" data-cy="closeButton" outlined small>Close</v-btn>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { formatDateAsMMDDYYYY } from "@/lib/formatters";
import testPlanApi from "@/api/testPlan";
import GenericLoader from "@/components/GenericLoader";

export default {
  components: {
    GenericLoader,
  },
  computed: {
    serviceAreaId() {
      const { id } = this.serviceAreaRecord;
      return id;
    },
    ...mapState({
      isLoading: state => state.testPlan.isLoading,
      historicalTestPlanStatusResult: state => state.testPlan.historicalTestPlanStatusResult,
    }),
  },
  async created() {
    try {
      this.historicallyNotImpactedCount = await testPlanApi.historicalTestPlanCount();
    } catch (e) {
      console.error(e);
      this.$notifyError("Could not fetch data.");
    }
  },
  data() {
    return {
      historicallyNotImpactedCount: 0,
      valid: true,
    };
  },
  methods: {
    close() {
      this.$emit("input", false);
      this.setHistoricalTestPlanStatusResult(null);
    },
    formatGoLiveDate(date) {
      if (!date) return "";
      return formatDateAsMMDDYYYY(date);
    },
    async reset() {
      try {
        await this.resetHistoricalTestPlanStatus(this.serviceAreaId);
        this.$notifySuccess({ message: "Testing status updated." });
      } catch (e) {
        console.error(e);
        return this.$notifyError({ message: "Error updating data." });
      }
      this.historicallyNotImpactedCount = await testPlanApi.historicalTestPlanCount();
    },
    async update() {
      try {
        await this.setHistoricalTestPlanStatus(this.serviceAreaId);
        this.$notifySuccess({ message: "Testing status updated." });
      } catch (e) {
        console.error(e);
        return this.$notifyError({ message: "Error updating data." });
      }
      this.historicallyNotImpactedCount = await testPlanApi.historicalTestPlanCount();
    },
    ...mapActions({
      resetHistoricalTestPlanStatus: "testPlan/resetHistoricalTestPlanStatus",
      setHistoricalTestPlanStatus: "testPlan/setHistoricalTestPlanStatus",
    }),
    ...mapMutations({
      setHistoricalTestPlanStatusResult: "testPlan/setHistoricalTestPlanStatusResult",
    }),
  },
  props: {
    serviceAreaRecord: { default: null, required: true, type: Object },
    value: { required: true, type: Boolean },
  },
};
</script>
